import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { PortfolioCard } from '@/components/dashboard/PortfolioCard';
import { PendingApprovalsCard } from '@/components/dashboard/PendingApprovalsCard';
import { PositionsTable } from '@/components/dashboard/PositionsTable';
import { RiskLimitCard } from '@/components/dashboard/RiskLimitCard';
import { PlatformStatus } from '@/components/dashboard/PlatformStatus';
import { AgentControlPanel, AgentDecisionLog } from '@/components/agent';
import { PDTStatusCard } from '@/components/compliance';
import { RouteFallback } from '@/components/common';
import { OrdersTable } from '@/components/trading/OrdersTable';
import { usePortfolioStore } from '@/stores/portfolioStore';
import { useAgentStore } from '@/stores/agentStore';
import { portfolioService } from '@/services/portfolio';
import { useTrading } from '@/hooks/useTrading';
import { useWebSocketEvents } from '@/hooks/useWebSocketEvents';
import type { ChartPeriod } from '@/lib/constants';
import type { PerformanceDataPoint } from '@/components/charts/PerformanceChart';

// Trading Agent is otherwise eager (App.tsx keeps it out of the app-level
// code split — it's core platform functionality, not an on-demand app), but
// PerformanceChart is the ONE place recharts enters this page's graph.
// Lazy-loading just this component keeps recharts out of the entry bundle
// (FR-032/T064) without pulling the rest of the dashboard behind Suspense.
const PerformanceChart = lazy(() =>
  import('@/components/charts/PerformanceChart').then((m) => ({ default: m.PerformanceChart })),
);

export function Dashboard() {
  const { setPortfolioSummary, setPositions, setLoading, setError } = usePortfolioStore();
  const { setAgentState, addDecision } = useAgentStore();
  const { orders, isLoadingOrders } = useTrading();
  const queryClient = useQueryClient();
  const [chartPeriod, setChartPeriod] = useState('1M');

  // Wire WebSocket events to refetch queries and update stores in real-time
  useWebSocketEvents({
    onPortfolioUpdate: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', 'history'] });
    },
    onOrderUpdate: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', 'history'] });
    },
    onAgentStatus: (data) => {
      if (data && typeof data === 'object') {
        const d = data as unknown as Record<string, unknown>;
        setAgentState({
          status: String(d.state ?? 'stopped') as import('@/stores/agentStore').AgentStatus,
          strategyName: String(d.strategy_name ?? 'default'),
          lastAction: d.message ? String(d.message) : null,
          lastActionAt: d.last_activity ? String(d.last_activity) : null,
          dailyPnl: 0,
          tradesToday: Number(d.trades_today ?? 0),
          errorMessage: null,
          startedAt: null,
        });
      }
    },
    onAgentDecision: (data) => {
      if (data && typeof data === 'object') {
        const d = data as unknown as Record<string, unknown>;
        addDecision({
          id: String(d.decision_id ?? ''),
          decisionType: String(
            d.decision_type ?? 'hold'
          ) as import('@/stores/agentStore').AgentDecision['decisionType'],
          symbol: String(d.symbol ?? ''),
          quantity: null,
          price: null,
          notional: d.notional != null ? Number(d.notional) : null,
          reasoning: String(d.reasoning ?? ''),
          confidence: Number(d.confidence ?? 0),
          outcome: 'pending',
          createdAt: new Date().toISOString(),
        });
      }
    },
    onRiskStatus: () => {
      queryClient.invalidateQueries({ queryKey: ['risk'] });
    },
    onPdtStatus: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance'] });
    },
    onPlatformStatus: () => {
      queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onNotification: () => {
      // Notifications are handled by the header/notification bell
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Filter to only show pending/submitted orders
  const pendingOrders = useMemo(
    () => orders.filter((order) => order.status === 'pending' || order.status === 'submitted'),
    [orders]
  );

  // Fetch portfolio summary
  const summaryQuery = useQuery({
    queryKey: ['portfolio', 'summary'],
    queryFn: () => portfolioService.getPortfolioSummary(),
    refetchInterval: 10000, // Refresh every 10s
  });

  // Fetch positions
  const positionsQuery = useQuery({
    queryKey: ['portfolio', 'positions'],
    queryFn: () => portfolioService.getPositions(),
    refetchInterval: 10000,
  });

  // Fetch portfolio history for performance chart
  const historyQuery = useQuery({
    queryKey: ['portfolio', 'history', chartPeriod],
    queryFn: () => portfolioService.getHistory(chartPeriod),
    refetchInterval: 15000,
  });

  // Transform portfolio history to PerformanceDataPoint format
  const performanceData: PerformanceDataPoint[] = useMemo(() => {
    if (!historyQuery.data) return [];
    return historyQuery.data.map((item) => ({
      timestamp: item.timestamp,
      value: item.total_value,
    }));
  }, [historyQuery.data]);

  // Update store when data changes
  useEffect(() => {
    if (summaryQuery.data) {
      setPortfolioSummary(summaryQuery.data);
    }
  }, [summaryQuery.data, setPortfolioSummary]);

  useEffect(() => {
    if (positionsQuery.data) {
      setPositions(positionsQuery.data);
    }
  }, [positionsQuery.data, setPositions]);

  useEffect(() => {
    setLoading(summaryQuery.isLoading || positionsQuery.isLoading);
  }, [summaryQuery.isLoading, positionsQuery.isLoading, setLoading]);

  useEffect(() => {
    const error = summaryQuery.error || positionsQuery.error;
    setError(error ? (error).message : null);
  }, [summaryQuery.error, positionsQuery.error, setError]);

  const isLoading = summaryQuery.isLoading || positionsQuery.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-semibold"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--fg)',
              letterSpacing: '-0.015em',
            }}
          >
            Portfolio Dashboard
          </h1>
          <p
            style={{
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              marginTop: 4,
            }}
          >
            Monitor your trading performance in real-time
          </p>
        </div>
      </div>

      {/* Performance chart */}
      <Suspense fallback={<RouteFallback />}>
        <PerformanceChart
          data={performanceData}
          isLoading={historyQuery.isLoading}
          initialValue={performanceData[0]?.value}
          onPeriodChange={setChartPeriod as (period: ChartPeriod) => void}
        />
      </Suspense>

      {/* Portfolio summary and status cards — masonry-style columns */}
      <div className="columns-1 lg:columns-2 gap-6 space-y-6">
        <div className="break-inside-avoid">
          <PortfolioCard summary={summaryQuery.data || null} isLoading={isLoading} />
        </div>
        <div className="break-inside-avoid">
          <AgentControlPanel />
        </div>
        <div className="break-inside-avoid">
          <PlatformStatus />
        </div>
        <div className="break-inside-avoid">
          <RiskLimitCard />
        </div>
        <div className="break-inside-avoid">
          <PDTStatusCard />
        </div>
        <div className="break-inside-avoid">
          <PendingApprovalsCard />
        </div>
      </div>

      {/* Pending Orders */}
      {pendingOrders.length > 0 && (
        <div className="space-y-4">
          <h2
            className="text-lg font-semibold"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--fg)',
              letterSpacing: '-0.01em',
            }}
          >
            Pending Orders
          </h2>
          <OrdersTable orders={pendingOrders} isLoading={isLoadingOrders} showCancelButton={true} />
        </div>
      )}

      {/* Positions table */}
      <div className="space-y-4">
        <h2
          className="text-lg font-semibold"
          style={{
            fontFamily: 'var(--font-display)',
            color: 'var(--fg)',
            letterSpacing: '-0.01em',
          }}
        >
          Positions
        </h2>
        <PositionsTable positions={positionsQuery.data || []} isLoading={isLoading} />
      </div>

      {/* Agent Decision Log */}
      <div className="space-y-4">
        <h2
          className="text-lg font-semibold"
          style={{
            fontFamily: 'var(--font-display)',
            color: 'var(--fg)',
            letterSpacing: '-0.01em',
          }}
        >
          Agent Activity
        </h2>
        <AgentDecisionLog />
      </div>
    </div>
  );
}

export default Dashboard;
